import React, { useEffect, useState } from "react"

import * as Tooltip from "@radix-ui/react-tooltip"
import Layout from "./layout"
import SEO from "./seo"
import CTA from "./cta"

import logo from "../images/logo-only.svg"
import LogoSVG from "../images/logo.svg"
import Button from "./Button"
import HeadlessCTA from "./headlessCTA"

import { DebounceInput } from "react-debounce-input"

// tanstack table
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  ColumnDef,
  getSortedRowModel,
  getFilteredRowModel,
  SortingState,
  filterFns,
} from "@tanstack/react-table"
import { toast } from "react-toastify"
import {
  IconArrowDown,
  IconArrowsDownUp,
  IconArrowUp,
  IconLock,
  IconSearch,
} from "@tabler/icons"

const DatasetDisplay = ({
  location,
  ogImage,
  dataset,
  dataContext,
  fullVersionOverride = false,
}) => {
  const params = new URLSearchParams(location.search)
  const version = params.get("v")
  const isFullVersion =
    version == hashCode(dataContext.title) || fullVersionOverride
  const datasetLimit = isFullVersion ? 99999 : dataContext.freeLimit
  const [sorting, setSorting] = useState([])
  const [globalFilter, setGlobalFilter] = useState("")

  const table = useReactTable({
    data: dataset.slice(0, datasetLimit),
    columns: dataContext.columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: filterFns.includesString,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getColumnCanGlobalFilter: col => col.id === "accountName",
  })

  useEffect(() => {
    if (isFullVersion) {
      toast(
        "This is the full dataset. You can bookmark this page to come back anytime. Enjoy!"
      )
    }
  }, [])

  return (
    <Layout variant="plain">
      <SEO
        title={dataContext.title}
        description={dataContext.description}
        image={ogImage}
      />

      <section className="bg-white mb-12">
        <div className="max-w-3xl py-8 mx-auto px-2 md:px-6">
          <Button to="/data-points">&larr; Back to Data Marketplace</Button>
          <h1
            id="overview"
            className="scroll-mt-24 text-3xl md:text-5xl font-bold my-4"
          >
            {dataContext.title} {isFullVersion ? "[FULL]" : ""}
          </h1>
          <p className="text-dark-grey text-sm mb-2">{dataContext.blurb}</p>
          <div className="flex text-sm py-4">
            <dl className="flex-1">
              <dt className="text-dark-grey">Author</dt>
              <dd className="font-semibold">Rudy Lai</dd>
            </dl>
            <dl className="flex-1">
              <dt className="text-dark-grey">Published</dt>
              <dd className="font-semibold">{dataContext.published}</dd>
            </dl>
            <Tooltip.Provider delayDuration={0}>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <dl className="flex-1 cursor-pointer hover:bg-light-grey rounded-lg transition">
                    <dt className="text-dark-grey">Updates every</dt>
                    <dd className="font-semibold">Quarter</dd>
                  </dl>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    className="TooltipContent bg-black shadow-lg font-sans rounded-lg text-xs py-1 px-2 text-white"
                    side="top"
                    sideOffset={12}
                    align="center"
                  >
                    To get weekly alerts and updates from Tactic, request a demo
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          </div>
        </div>

        <div className="container mx-auto flex flex-col md:flex-row justify-between mb-4 md:items-center px-1">
          <div className="font-semibold text-lg">
            {!isFullVersion && `${dataContext.freeLimit}/`}
            {dataset.length} companies
          </div>
          <div className="flex gap-2">
            <div class="relative flex items-center">
              <IconSearch className="absolute text-primary-grey-dark w-4 h-4 ml-3" />
              <DebounceInput
                value={globalFilter ?? ""}
                onChange={value => setGlobalFilter(value.target.value)}
                className="w-full pl-8 shadow-button outline-lilac border 
                rounded-lg py-2 px-4 border-medium-grey placeholder:text-dark-grey
                hover:border-dark-grey min-w-[250px]"
                placeholder="Search company names"
              />
            </div>
            <Tooltip.Provider delayDuration={0}>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <div>
                    <Button variation="secondary">
                      Export <IconLock className="text-dark-grey" size={16} />
                    </Button>
                  </div>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    className="TooltipContent bg-black shadow-lg font-sans rounded-lg text-xs py-1 px-2 text-white"
                    side="left"
                    sideOffset={12}
                    align="center"
                  >
                    Request a demo with Tactic to export data
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          </div>
        </div>

        <div
          className="container mx-auto border rounded-lg bg-light-grey overflow-x-auto 
          overflow-y-auto text-sm max-h-[600px] mb-12 shadow-lg"
        >
          <div class="table min-w-full font-sans text-left mb-2">
            <div
              className="table-header-group text-dark-grey bg-light-grey sticky top-0
              border-b border-medium-grey text-xs whitespace-nowrap z-10"
            >
              {table.getHeaderGroups().map(headerGroup => (
                <div className="table-row shadow-md" key={headerGroup.id}>
                  <div
                    className="hidden md:table-cell p-3 md:sticky md:left-0 top-0
                  bg-light-grey text-light-grey"
                  >
                    #
                  </div>
                  {headerGroup.headers.map(header => (
                    <div
                      className={`table-cell p-3 ${
                        header.column.getCanSort() &&
                        "hover:bg-medium-grey cursor-pointer"
                      }`}
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {{
                        asc: (
                          <IconArrowUp
                            size={12}
                            className="inline -mt-1 ml-1"
                          />
                        ),
                        desc: (
                          <IconArrowDown
                            size={12}
                            className="inline -mt-1 ml-1"
                          />
                        ),
                      }[header.column.getIsSorted()] ?? (
                        <IconArrowsDownUp
                          size={12}
                          className="inline -mt-1 ml-1"
                        />
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="table-row-group text-black border-t border-b shadow-sm bg-white">
              {table.getRowModel().rows.map(row => (
                <div className="table-row" key={row.id}>
                  <div
                    className="hidden md:table-cell p-3 border-b border-light-grey md:sticky md:left-0
                      bg-white text-medium-grey"
                  >
                    {row.index + 1}
                  </div>
                  {row.getVisibleCells().map(cell => (
                    <div
                      className="table-cell p-3 border-b border-light-grey whitespace-nowrap"
                      key={cell.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-light-grey w-full sticky bottom-0 left-0 py-2 px-4 flex items-center justify-end">
            <div className="flex gap-2 items-center">
              <span className="text-dark-grey text-xs">
                Customisable data by
              </span>{" "}
              <img src={LogoSVG} className="w-16" />
            </div>
          </div>
        </div>

        <div className="bg-lilac h-24"></div>
        <div
          className={
            isFullVersion
              ? "w-full bg-lilac"
              : "sticky z-20 bottom-0 w-full bg-lilac"
          }
        >
          <div className="container py-4 px-6 lg:px-0 mx-auto flex flex-col lg:flex-row items-center gap-2 lg:gap-12 lg:justify-between">
            {isFullVersion ? (
              <>
                <p className="text-white text-sm lg:text-xl">
                  If you'd like to ask additional questions (e.g. who has
                  monthly pricing?), integrate this into your CRM, or access the
                  rest of the dataset, please contact us for a chat!
                </p>
                <HeadlessCTA
                  buttonLabel="Request Demo"
                  source={dataContext.title}
                />
              </>
            ) : (
              <>
                <p className="text-white text-sm lg:text-xl">
                  {dataContext.ctaText}
                </p>
                <HeadlessCTA
                  buttonLabel="Show me the list!"
                  source={dataContext.title}
                  nextPage={`?v=${hashCode(dataContext.title)}`}
                />
              </>
            )}
          </div>
        </div>
        <div className="bg-lilac h-24"></div>

        <div className="mx-auto py-8 md:py-16 px-6 lg:px-0 prose lg:prose-lg text-gray-600">
          {dataContext.blogPost}
        </div>

        <div className="container mx-auto">
          <div className="flex items-center gap-6">
            <div className="h-px bg-medium-grey flex-1"></div>
            <img src={logo} className="h-12 mx-auto my-12" />
            <div className="h-px bg-medium-grey flex-1"></div>
          </div>
        </div>
      </section>
      <div id="cta">
        <CTA />
      </div>
    </Layout>
  )
}

function hashCode(str) {
  let hash = 0
  for (let i = 0, len = str.length; i < len; i++) {
    let chr = str.charCodeAt(i)
    hash = (hash << 5) - hash + chr
    hash |= 0 // Convert to 32bit integer
  }
  return hash
}

export default DatasetDisplay
